export interface PlaceHoldInfoImpl {
  message: string;
  guardiansBlocking: string[];
  success: boolean;
  bibliographicId: string;
  pickupBranchId: string;
  titleLevelHold: boolean;
  interfaceType: "PAC" | "KIDS";
  downloadable: boolean;
  serialItem: boolean;
}
export interface PlaceHoldReturnImpl {
  hostSystemDiag: HostSystemDiagImpl;
  placeHoldInfos: PlaceHoldInfoImpl[];
}
export interface CancelHoldInfoImpl {
  desireNumber: number;
  whichBranch: number;
  bibliographicId: string;
  holdQueueLength: number;
  success: boolean;
  downloadable: boolean;
  cancelable: boolean;
  status: string;
  messageToken: string;
}
export interface CancelHoldReturnImpl {
  hostSystemDiag: HostSystemDiagImpl;
  cancelHoldInfos: CancelHoldInfoImpl[];
}
export interface SuspendHoldInfo {
  desireNumber: number;
  success: boolean;
  suspendDate: Date;
  queuePosition: number;
  bibliographicId: string;
  pickupBranchId: string;
  downloadable: boolean;
  suspendable: boolean;
}
export interface SuspendHoldReturn {
  hostSystemDiag: HostSystemDiagImpl;
  suspendHoldInfos: SuspendHoldInfo[];
}
import axios from "axios";
import { ResourceDWR } from "../../dto/SuggestionResult";
import { HostSystemItemSortType } from "../../dto/HostSystemItemSortType";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { HostSystemDiagImpl } from "../../dto/HostSystemDiagImpl";

export const placeHoldRequest = async (
  placeHoldInfos: PlaceHoldInfoImpl[],
  limitTitleLevelHoldsToPatronBranch = false
): Promise<PlaceHoldReturnImpl> => {
  const response = await axios.post<PlaceHoldReturnImpl>(
    `/requests/${limitTitleLevelHoldsToPatronBranch}`,
    {
      placeHoldInfos: JSON.stringify(placeHoldInfos),
    }
  );
  return response.data;
};

export const cancelHold = async (
  holds: HostSystemItemView[]
): Promise<CancelHoldReturnImpl> => {
  const cancelHoldInfos: Omit<
    CancelHoldInfoImpl,
    "cancelable" | "messageToken"
  >[] = holds.map(
    ({
      holdNumber,
      holdQueueLength,
      bibliographicId,
      holdPickupBranchId,
      status,
      downloadable,
    }) => {
      return {
        desireNumber: parseInt(holdNumber),
        success: false,
        holdQueueLength: parseInt(holdQueueLength),
        bibliographicId: bibliographicId,
        whichBranch: holdPickupBranchId,
        status: status,
        downloadable: downloadable,
      };
    }
  );
  const response = await axios.post<CancelHoldReturnImpl>("/requests/cancel", {
    cancelHoldInfos: JSON.stringify(cancelHoldInfos),
  });
  return response.data;
};

export const suspendHold = async (
  holds: HostSystemItemView[],
  suspendUntil: Date
): Promise<SuspendHoldReturn> => {
  // TODO confirm discrepencies in property types for SuspendHoldInfo and HostSystemItemView
  // (parseInt and toString below)

  const suspendHoldInfos = holds.map(
    ({
      holdNumber,
      holdQueueLength,
      bibliographicId,
      holdPickupBranchId,
      downloadable,
    }) => {
      const holdInfo: Omit<SuspendHoldInfo, "suspendable"> = {
        desireNumber: parseInt(holdNumber),
        success: false,
        suspendDate: suspendUntil,
        queuePosition: parseInt(holdQueueLength),
        bibliographicId,
        pickupBranchId: holdPickupBranchId.toString(),
        downloadable: downloadable,
      };
      return holdInfo;
    }
  );
  const response = await axios.post<SuspendHoldReturn>("/requests/suspend", {
    suspendHoldInfos: JSON.stringify(suspendHoldInfos),
  });
  return response.data;
};

export const unsuspendHold = async (
  holds: HostSystemItemView[]
): Promise<SuspendHoldReturn> => {
  const suspendHoldInfos = holds.map(
    ({
      holdNumber,
      holdQueueLength,
      bibliographicId,
      holdPickupBranchId,
      downloadable,
    }) => {
      const holdInfo: Omit<SuspendHoldInfo, "suspendable"> = {
        desireNumber: parseInt(holdNumber),
        success: false,
        suspendDate: null,
        queuePosition: parseInt(holdQueueLength),
        bibliographicId,
        pickupBranchId: holdPickupBranchId.toString(),
        downloadable: downloadable,
      };
      return holdInfo;
    }
  );

  const response = await axios.post<SuspendHoldReturn>("/requests/unsuspend", {
    suspendHoldInfos: JSON.stringify(suspendHoldInfos),
  });
  return response.data;
};

export interface GetAllHoldsReturn {
  holds: HostSystemItemView[];
  // Add any other relevant properties here based on your needs
}

export interface EResourceFormat {
  formatId: string;
  formatToken: string;
  availableOn: Date;
  preRelease: boolean;
}

export const getAllHoldsSortable = async (
  page: number,
  count: number,
  sortType: HostSystemItemSortType,
  includeResources = true
): Promise<GetAllHoldsReturn> => {
  const start = page * count;
  const response = await axios.get<GetAllHoldsReturn>(
    `/requests/${start}/${count}/${sortType}/${includeResources}`
  );
  return response.data;
};
